// EXPERIMENT: psf_featured_spiking_petition_experiment — remove with experiment cleanup
import { useCallback, useMemo } from 'react';

import qs from 'qs';

import { useTracking } from '@change-corgi/core-react/tracking';

import { usePsfPromoteOrSharePageData } from '#src/app/pages/psf/promoteOrShare/pageContext';
import { useCountryCode } from '#src/app/shared/hooks/l10n';

import { useFeaturedSpikingPetitionExperiment } from '../useFeaturedSpikingPetitionExperiment';

/**
 * Treats the experiment on mount (every arm). In `v1`, Skip goes to the featured petition page, which
 * continues to `skipUrl`. In `fake_control`, Skip fires `featured_psf_step_would_have_seen`, so the analysis
 * can compare `v1` with the `fake_control` signers who would have seen the step.
 */
export function useFeaturedSpikingPetitionSkip(
	skipUrl: string,
): ModelHookResult<{ skipHref: string }, { handleFeaturedSkipClick: () => void }> {
	const {
		data: {
			petition: { slug },
			featuredSpikingPetition,
		},
	} = usePsfPromoteOrSharePageData();
	const {
		data: { arm },
	} = useFeaturedSpikingPetitionExperiment();
	const track = useTracking();
	const countryCode = useCountryCode();

	const skipHref = useMemo(
		() =>
			arm === 'v1' && featuredSpikingPetition
				? `/p/${encodeURIComponent(slug)}/psf/featured-petition${qs.stringify({ next: skipUrl }, { addQueryPrefix: true })}`
				: skipUrl,
		[arm, featuredSpikingPetition, slug, skipUrl],
	);

	const handleFeaturedSkipClick = useCallback(() => {
		if (arm !== 'fake_control' || !featuredSpikingPetition) return;
		// The same properties as the step's events. The combo page adds the host `petition_id`.
		void track('featured_psf_step_would_have_seen', {
			featured_petition_id: `petition_${featuredSpikingPetition.id}`,
			featured_psf_step_variation: arm,
			user_country_code: countryCode,
			context: 'combo_psf_skip',
		});
	}, [arm, featuredSpikingPetition, track, countryCode]);

	return { data: { skipHref }, actions: { handleFeaturedSkipClick } };
}
